import React from 'react';
import {
  ArrowRight, Truck, Zap, ShieldCheck, MessageCircle, Lock,
  ClipboardList, Users, UserCheck, PackageCheck, Search, Euro, MapPin,
} from 'lucide-react';

const HERO_IMAGE =
  'https://customer-assets-eiarnc6j.emergentagent.net/job_driver-marketplace-16/artifacts/gwl8dbro_file_000000009d1c81f4b25bb23e3c10d474.png';

const TRUST_CHIPS = [
  { Icon: ShieldCheck, label: 'Verified drivers' },
  { Icon: MessageCircle, label: 'Chat with drivers' },
  { Icon: Lock, label: 'Secure booking' },
];

const STEPS = [
  { Icon: ClipboardList, title: 'Post your job', desc: "Tell us what you're moving." },
  { Icon: Users, title: 'Get driver offers', desc: 'Drivers send you their prices.' },
  { Icon: UserCheck, title: 'Choose your driver', desc: 'Compare profiles and pick one.' },
  { Icon: PackageCheck, title: 'Get it delivered', desc: 'Your driver does the rest.' },
];

const BENEFITS = [
  { Icon: Euro, title: 'Competitive prices', desc: 'Drivers compete for your job.' },
  { Icon: UserCheck, title: 'You choose the driver', desc: 'See profiles, reviews and experience.' },
  { Icon: MessageCircle, title: 'Chat before you book', desc: "Make sure they're right for the job." },
  { Icon: MapPin, title: 'Drivers across Ireland', desc: 'From Dublin to Donegal.' },
];

/** Photographic hero + primary CTAs + trust chips. */
export const HomeHero = ({ onPost, onQuickPost }) => (
  <section className="mb-5 overflow-hidden rounded-2xl bg-white border border-gray-200/80 shadow-sm" data-testid="customer-home-hero">
    <div className="relative">
      <img
        src={HERO_IMAGE}
        alt="Sofa, boxes and household items ready for collection outside a house"
        className="absolute inset-0 w-full h-full object-cover object-center sm:object-right"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/20 sm:to-transparent" />
      <div className="relative px-5 pt-6 pb-5 sm:px-8 sm:pt-10 sm:pb-8 max-w-xl">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-[1.05] text-[#050505]">
          Need something<br />
          <span className="text-[#21873E]">delivered?</span>
        </h1>
        <p className="mt-2.5 text-sm sm:text-base font-medium text-[#374151] max-w-xs sm:max-w-sm">
          Post your delivery and let local drivers compete for your job.
        </p>

        <button
          onClick={onPost}
          className="mt-5 w-full sm:w-auto group inline-flex items-center justify-center gap-3 pl-4 pr-5 py-4 rounded-full bg-[#F57C00] hover:bg-[#E65100] text-white font-extrabold text-base sm:text-lg shadow-lg shadow-orange-500/25 transition-all active:scale-[0.98]"
          data-testid="home-hero-post-btn"
        >
          <span className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center">
            <Truck className="w-5 h-5" strokeWidth={2.25} />
          </span>
          Post a Delivery
          <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
        </button>

        <button
          onClick={onQuickPost}
          className="mt-2.5 w-full sm:w-auto group inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white border border-amber-200 hover:border-amber-400 hover:bg-amber-50/60 transition-all active:scale-[0.98]"
          data-testid="home-hero-quick-post-btn"
        >
          <Zap className="w-4 h-4 text-amber-500" fill="currentColor" />
          <span className="text-sm font-bold text-[#050505]">Need it done quickly?</span>
          <span className="text-sm font-extrabold text-[#F57C00]">Quick Post</span>
          <ArrowRight className="w-4 h-4 text-[#F57C00] transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>

    <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100 bg-white" data-testid="trust-badges">
      {TRUST_CHIPS.map(({ Icon, label }) => (
        <div key={label} className="flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 px-2 py-3 text-center">
          <Icon className="w-4 h-4 text-[#2E7D32] flex-shrink-0" strokeWidth={2} />
          <span className="text-[11px] sm:text-xs font-semibold text-[#374151] leading-tight">{label}</span>
        </div>
      ))}
    </div>
  </section>
);

/** 4-step explainer. */
export const HowItWorks = () => (
  <section className="mb-5 rounded-2xl bg-white border border-gray-200/80 p-5 sm:p-6" data-testid="customer-how-it-works">
    <h2 className="text-lg md:text-lg font-bold tracking-tight text-[#050505] mb-4">How JustDeliver works</h2>
    <ol className="grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-5">
      {STEPS.map(({ Icon, title, desc }, i) => (
        <li key={title} className="relative flex flex-col items-center text-center" style={{ animationDelay: `${i * 60}ms` }}>
          <div className="relative">
            <span className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100">
              <Icon className="w-6 h-6 text-[#2E7D32]" strokeWidth={1.75} />
            </span>
            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#F57C00] text-white text-xs font-bold flex items-center justify-center ring-2 ring-white">
              {i + 1}
            </span>
          </div>
          <p className="mt-2.5 text-sm font-bold text-[#050505] leading-snug">{title}</p>
          <p className="mt-0.5 text-xs text-gray-500 leading-snug max-w-[11rem]">{desc}</p>
        </li>
      ))}
    </ol>
  </section>
);

/** Directory entry point. */
export const FindDriverCard = ({ onSearch }) => (
  <section
    className="mb-5 rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-[#E8F7EC] via-white to-[#E8F7EC] p-5 sm:p-6"
    data-testid="customer-find-driver-card"
  >
    <div className="flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-bold tracking-tight text-[#050505]">Find a driver near you</h2>
        <p className="mt-1 text-sm text-[#4B5563]">
          Search our driver directory and find a trusted driver for your delivery.
        </p>
        <button
          onClick={onSearch}
          className="mt-4 group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-sm shadow-md shadow-emerald-900/10 transition-all active:scale-[0.98]"
          data-testid="customer-search-drivers-btn"
        >
          <Search className="w-4 h-4" />
          Search Drivers
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
      <div className="hidden sm:flex flex-shrink-0 w-28 h-28 rounded-2xl bg-white/70 border border-emerald-100 items-center justify-center">
        <MapPin className="w-12 h-12 text-[#2E7D32]" strokeWidth={1.5} />
      </div>
    </div>
  </section>
);

/** Benefit grid. */
export const WhyJustDeliver = () => (
  <section className="mb-5 rounded-2xl bg-white border border-gray-200/80 p-5 sm:p-6" data-testid="why-choose-justdeliver">
    <h2 className="text-lg font-bold tracking-tight text-[#050505] mb-4">Why people use JustDeliver</h2>
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="why-choose-list">
      {BENEFITS.map(({ Icon, title, desc }) => (
        <li key={title} className="flex items-start gap-3 p-3.5 rounded-xl bg-gray-50/80 border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/40 transition-colors">
          <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center">
            <Icon className="w-4.5 h-4.5 text-[#2E7D32]" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-[#050505] leading-snug">{title}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">{desc}</p>
          </div>
        </li>
      ))}
    </ul>
  </section>
);
